import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { addDays, addMonths, addWeeks, endOfMonth, endOfWeek, format, isSameDay, isSameMonth, parseISO, startOfMonth, startOfWeek } from "date-fns";
import { ChevronLeft, ChevronRight, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PageHeader, PageState, StatusPill, Field } from "@/components/app/Common";
import { SimpleSelect, entityOptions } from "@/components/app/SimpleSelect";
import { useLoad } from "@/hooks/useLoad";
import { api, errMsg } from "@/lib/api";

const KINDS = { "Corporate Filings": "#3B82F6", "Tax Filings": "#F59E0B", "Annual Returns": "#10B981", "Revival Items": "#A855F7", "Corporate Events": "#94A3B8" };

const Chip = ({ it }) => (
  <div className="text-[11px] truncate rounded px-1.5 py-0.5 border-l-2 bg-muted/60" style={{ borderColor: KINDS[it.kind] }} title={`${it.title} · ${it.entity_name || ""}`}>{it.title}</div>
);

const MonthView = ({ cursor, items }) => {
  const start = startOfWeek(startOfMonth(cursor));
  const days = [];
  for (let d = start; d <= endOfWeek(endOfMonth(cursor)); d = addDays(d, 1)) days.push(d);
  return (
    <div className="surface overflow-hidden" data-testid="calendar-month-view">
      <div className="grid grid-cols-7 text-xs text-muted-foreground border-b border-border">{["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => <div key={d} className="px-2 py-2">{d}</div>)}</div>
      <div className="grid grid-cols-7">
        {days.map((d) => {
          const its = items.filter((i) => isSameDay(parseISO(i.date), d));
          return (
            <div key={d.toISOString()} className={`min-h-[96px] border-b border-r border-border p-1.5 space-y-1 ${isSameMonth(d, cursor) ? "" : "opacity-40"}`}>
              <div className={`text-xs font-mono ${isSameDay(d, new Date()) ? "inline-flex h-5 w-5 items-center justify-center rounded-full bg-primary text-primary-foreground" : "text-muted-foreground"}`}>{format(d, "d")}</div>
              {its.slice(0, 3).map((it) => <Chip key={it.id} it={it} />)}
              {its.length > 3 && <div className="text-[10px] text-muted-foreground">+{its.length - 3} more</div>}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const WeekView = ({ cursor, items }) => {
  const start = startOfWeek(cursor);
  return (
    <div className="grid md:grid-cols-7 gap-3" data-testid="calendar-week-view">
      {[...Array(7)].map((_, i) => {
        const d = addDays(start, i);
        const its = items.filter((x) => isSameDay(parseISO(x.date), d));
        return (
          <div key={i} className={`surface p-3 min-h-[160px] ${isSameDay(d, new Date()) ? "border-primary/50" : ""}`}>
            <div className="text-xs text-muted-foreground">{format(d, "EEE")}</div>
            <div className="font-mono text-lg">{format(d, "d")}</div>
            <div className="space-y-1.5 mt-2">{its.map((it) => <Chip key={it.id} it={it} />)}</div>
          </div>
        );
      })}
    </div>
  );
};

const AgendaView = ({ items, onDelete }) => {
  const today = new Date().toISOString().slice(0, 10);
  const groups = {};
  items.filter((i) => i.date >= today || ["Overdue", "Critical"].includes(i.status)).forEach((i) => { (groups[i.date] ||= []).push(i); });
  const keys = Object.keys(groups).sort();
  if (!keys.length) return <div className="surface p-8 text-sm text-muted-foreground text-center">Nothing on the agenda.</div>;
  return (
    <div className="space-y-4" data-testid="calendar-agenda-view">
      {keys.map((k) => (
        <div key={k} className="surface p-4 flex gap-5">
          <div className="w-20 shrink-0"><div className="text-xs text-muted-foreground">{format(parseISO(k), "EEE")}</div><div className="font-mono text-sm">{format(parseISO(k), "MMM d, yy")}</div></div>
          <ul className="flex-1 space-y-2">
            {groups[k].map((it) => (
              <li key={it.id} className="flex items-center gap-3 text-sm" data-testid={`agenda-item-${it.id}`}>
                <span className="h-2 w-2 rounded-full shrink-0" style={{ background: KINDS[it.kind] }} />
                <span className="flex-1">{it.title} <span className="text-muted-foreground">· {it.entity_name || "General"}</span></span>
                <StatusPill status={it.status} />
                {it.deletable && <button onClick={() => onDelete(it)} className="p-1 text-muted-foreground hover:text-red-500" data-testid={`event-delete-${it.id}`}><Trash2 className="h-3.5 w-3.5" /></button>}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
};

const EventDialog = ({ open, onOpenChange, ents, onSaved }) => {
  const [f, setF] = useState({ title: "", date: "", entity_id: "" });
  const save = async (e) => {
    e.preventDefault();
    try {
      await api.post("/events", { ...f, entity_id: f.entity_id || null });
      toast.success("Event added");
      onSaved();
      onOpenChange(false);
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="event-dialog">
        <DialogHeader><DialogTitle>Add corporate event</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-4">
          <Field label="Title"><Input required value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="Annual general meeting" data-testid="event-title-input" /></Field>
          <Field label="Date"><Input required type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="event-date-input" /></Field>
          <Field label="Entity"><SimpleSelect value={f.entity_id} onChange={(v) => setF({ ...f, entity_id: v })} allowNone scoped noneLabel="General" options={entityOptions(ents)} testId="event-entity-select" /></Field>
          <Button type="submit" className="w-full" data-testid="event-save-btn">Save</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default function CalendarPage() {
  const [params] = useSearchParams();
  const [view, setView] = useState(params.get("view") || "month");
  const [cursor, setCursor] = useState(new Date());
  const [hidden, setHidden] = useState([]);
  const [open, setOpen] = useState(false);
  const state = useLoad(() => Promise.all([api.get("/calendar"), api.get("/entities")]).then(([a, b]) => ({ items: a.data, ents: b.data })));
  const items = useMemo(() => (state.data?.items || []).filter((i) => !hidden.includes(i.kind)), [state.data, hidden]);
  const move = (n) => setCursor(view === "week" ? addWeeks(cursor, n) : addMonths(cursor, n));
  return (
    <div>
      <PageHeader eyebrow="Module 8" title="Filing calendar" subtitle="Corporate filings, tax filings, annual returns, revival items and corporate events.">
        <Button onClick={() => setOpen(true)} data-testid="add-event-btn"><Plus className="h-4 w-4 mr-1" />Add event</Button>
      </PageHeader>
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div className="inline-flex rounded-lg border border-border p-1">
          {["month", "week", "agenda"].map((v) => (
            <button key={v} onClick={() => setView(v)} data-testid={`view-${v}`} className={`px-3 py-1 text-sm rounded-md capitalize ${view === v ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>{v}</button>
          ))}
        </div>
        {view !== "agenda" && (
          <div className="flex items-center gap-1">
            <Button size="icon" variant="ghost" onClick={() => move(-1)} data-testid="cal-prev"><ChevronLeft className="h-4 w-4" /></Button>
            <Button size="sm" variant="outline" onClick={() => setCursor(new Date())} data-testid="cal-today">Today</Button>
            <Button size="icon" variant="ghost" onClick={() => move(1)} data-testid="cal-next"><ChevronRight className="h-4 w-4" /></Button>
            <span className="font-semibold ml-2" data-testid="cal-label">{view === "week" ? `Week of ${format(startOfWeek(cursor), "MMM d, yyyy")}` : format(cursor, "MMMM yyyy")}</span>
          </div>
        )}
        <div className="flex flex-wrap gap-2 ml-auto">
          {Object.entries(KINDS).map(([k, c]) => (
            <button key={k} onClick={() => setHidden(hidden.includes(k) ? hidden.filter((x) => x !== k) : [...hidden, k])} data-testid={`kind-toggle-${k.toLowerCase().replace(/\s+/g, "-")}`}
              className={`flex items-center gap-1.5 text-xs px-2 py-1 rounded-full border border-border ${hidden.includes(k) ? "opacity-40" : ""}`}>
              <span className="h-2 w-2 rounded-full" style={{ background: c }} />{k}
            </button>
          ))}
        </div>
      </div>
      <PageState state={state}>
        {view === "month" && <MonthView cursor={cursor} items={items} />}
        {view === "week" && <WeekView cursor={cursor} items={items} />}
        {view === "agenda" && <AgendaView items={items} onDelete={async (it) => { await api.delete(`/events/${it.id}`); state.reload(); }} />}
      </PageState>
      <EventDialog open={open} onOpenChange={setOpen} ents={state.data?.ents} onSaved={state.reload} />
    </div>
  );
}
